import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../redux/loader/loaderActions";
import { Circles } from "react-loader-spinner";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PatientViewModal from "./components/PatientViewModal";
import baseUrl from "../../api/baseUrl";
import BottomTab from "../../components/pagination/BottomTab";


interface Patient {
  regNo: string;
  fullName: string;
  address: string;
  nic: string;
  mobileNo: string;
  dob: string;
  _id: string;
  new?: boolean;
}

// Mobile Card Component
const PatientCard: React.FC<{
  patient: Patient;
  onView: (id: string) => void;
  onActivate: (id: string, e: React.MouseEvent) => void;
}> = React.memo(({ patient, onView, onActivate }) => {
  const formatAddress = () => {
    if (typeof patient?.address === "object" && patient?.address !== null) {
      const addr = patient.address as any;
      return `${addr.street || ""} ${addr.no || ""}, ${addr.city || ""}, ${
        addr.district || ""
      }`.trim();
    }
    return patient?.address || "";
  };

  return (
    <div className="rounded-2xl p-4 shadow-md border transition-colors bg-white border-slate-100 dark:bg-slate-800 dark:border-slate-700">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md bg-gradient-to-br from-indigo-400 to-indigo-500 dark:from-indigo-500 dark:to-indigo-600">
            {patient?.fullName?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-200">
              {patient?.fullName}
            </p>
            <span className="px-2 py-0.5 text-xs font-medium rounded-full border transition-colors bg-indigo-50 text-indigo-600 border-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
              {patient?.regNo}
            </span>
          </div>
        </div>
        <span className="px-2 py-1 text-xs font-medium rounded-full transition-colors bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300">
          INACTIVE
        </span>
      </div>

      {/* Details */}
      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <CalendarTodayIcon sx={{ fontSize: 16 }} className="text-slate-400" />
          <span>{patient?.dob?.split("T")[0]}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <PhoneIcon sx={{ fontSize: 16 }} className="text-slate-400" />
          <span>{patient?.mobileNo}</span>
        </div>
        <div className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
          <LocationOnIcon sx={{ fontSize: 16 }} className="text-slate-400" />
          <span className="line-clamp-2">{formatAddress()}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-700 transition-colors">
        <button
          onClick={() => onView(patient?._id)}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
        >
          <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
          View
        </button>
        <button
          onClick={(e) => onActivate(patient?._id, e)}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm font-medium bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
        >
          <CheckCircleIcon sx={{ fontSize: 18 }} />
          Activate
        </button>
      </div>
    </div>
  );
});

// Desktop Table Row Component
const PatientRow: React.FC<{
  patient: Patient;
  onView: (id: string) => void;
  onActivate: (id: string, e: React.MouseEvent) => void;
}> = React.memo(({ patient, onView, onActivate }) => {
  const formatAddress = () => {
    if (typeof patient?.address === "object" && patient?.address !== null) {
      const addr = patient.address as any;
      return `${addr.street || ""} ${addr.no || ""}, ${addr.city || ""}, ${
        addr.district || ""
      }, ${addr.province || ""}, ${addr.country || ""}`.trim();
    }
    return patient?.address || "";
  };

  return (
    <tr className="border-b transition-colors border-slate-100 hover:bg-slate-50/50 dark:border-slate-700 dark:hover:bg-slate-700/50">
      <td className="px-3 py-2">
        <span className="px-2 py-0.5 text-xs font-semibold rounded-full border transition-colors bg-indigo-50 text-indigo-600 border-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
          {patient?.regNo}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs shadow-md bg-gradient-to-br from-indigo-400 to-indigo-500 dark:from-indigo-500 dark:to-indigo-600">
            {patient?.fullName?.charAt(0)?.toUpperCase()}
          </div>
          <span className="font-medium text-sm text-slate-800 dark:text-slate-200">
            {patient?.fullName}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
        {patient?.dob?.split("T")[0]}
      </td>
      <td className="px-3 py-2 text-xs max-w-[180px] truncate text-slate-600 dark:text-slate-300">
        {formatAddress()}
      </td>
      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
        {patient?.mobileNo}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1.5">
          <button
            onClick={() => onView(patient?._id)}
            className="p-1.5 rounded-lg transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
            title="View"
          >
            <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
          </button>
          <button
            onClick={(e) => onActivate(patient?._id, e)}
            className="p-1.5 rounded-lg transition-colors bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
            title="Activate"
          >
            <CheckCircleIcon sx={{ fontSize: 16 }} />
          </button>
        </div>
      </td>
    </tr>
  );
});
function DeactivePatient() {
  const [data, setData] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewPatientData, setViewPatientData] = useState<any>(null);
  const [loadingPatient, setLoadingPatient] = useState(false);

  const dispatch = useDispatch();
  const [search, setsearch] = useState("");
  const [searchdob, setsearchdob] = useState("");

  // Theme sync from Layouts.tsx
  useEffect(() => {
    const storedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    if (storedTheme) {
      setTheme(storedTheme);
    }

    const observer = new MutationObserver(() => {
      const currentTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark" | null;
      if (currentTheme && currentTheme !== theme) {
        setTheme(currentTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, [theme]);

  const handleClickGoViewPatient = useCallback(
    (id: string) => {
      setLoadingPatient(true);
      setViewModalOpen(true);
      baseUrl
        .get("patient/findById", {
          headers: {
            patientid: id,
          },
        })
        .then((response: any) => {
          setViewPatientData(response.data.data);
          setLoadingPatient(false);
        })
        .catch((error: any) => {
          console.error(error);
          setLoadingPatient(false);
        });
    },
    []
  );

  const closeViewModal = useCallback(() => {
    setViewModalOpen(false);
    setViewPatientData(null);
  }, []);

  const getdata = useCallback(
    (searchTerm: string, pages: number) => {
      dispatch(setloadeActive());
      setLoading(true);
      setError(null);
      baseUrl
        .get("client/patient/find-all-patients", {
          headers: {
            status: "false",
            page: pages,
            keyword: searchTerm,
          },
        })
        .then((response) => {
          dispatch(setloadeDeactive());
          setLoading(false);

          if (response.data.status === 204) {
            setData([]);
            setCount(0);
          } else {
            setData(response.data.data);
            setCount(response.data.count);
          }
        })
        .catch((error) => {
          dispatch(setloadeDeactive());
          setError(error.response?.data?.message || "Error fetching data");
          setLoading(false);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (data?.length < 19) {
      getdata("", page);
    }
  }, [page, getdata, data?.length]);

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      getdata(search, newPage);
    },
    [getdata, search]
  );

  const activePatient = useCallback(
    (patientId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      baseUrl
        .delete("patient/active", {
          headers: {
            patientid: patientId,
          },
        })
        .then(() => {
          getdata(search, page);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [getdata, search, page]
  );

  // Mobile Cards
  const mobileCards = useMemo(() => {
    return data?.map((patient: any) => (
      <PatientCard
        key={patient._id}
        patient={patient}
        onView={handleClickGoViewPatient}
        onActivate={activePatient}
      />
    ));
  }, [data, handleClickGoViewPatient, activePatient]);

  // Desktop Table Rows
  const renderedTableRows = useMemo(() => {
    return data?.map((patient) => (
      <PatientRow
        key={patient._id}
        patient={patient}
        onView={handleClickGoViewPatient}
        onActivate={activePatient}
      />
    ));
  }, [data, handleClickGoViewPatient, activePatient]);

  const handlePatientSearch = (e: any) => {
    setsearch(e.target.value);
    getdata(e.target.value, page);
  };

  const handleDobSearch = (e: any) => {
    setsearchdob(e.target.value);
  };

  const onDobSearch = () => {
    getdata(searchdob, page);
  };

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="rounded-[8px] shadow-lg border p-3 transition-colors bg-white border-slate-100 shadow-slate-200/50 dark:bg-slate-800 dark:border-slate-700 dark:shadow-slate-900/50">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2.5">
          {/* Search Inputs */}
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            {/* Name Search */}
            <div className="relative flex-1 max-w-md">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10"
                sx={{ fontSize: 18 }}
              />
              <input
                type="search"
                placeholder="Search patients..."
                onChange={handlePatientSearch}
                value={search}
                className="w-full h-9 pl-10 pr-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-xs font-medium placeholder:text-slate-400 bg-slate-50 border-slate-200 text-slate-700 focus:bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:bg-slate-600"
              />
            </div>

            {/* DOB Search */}
            <div className="flex gap-2">
              <div className="relative">
                <CalendarTodayIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10"
                  sx={{ fontSize: 16 }}
                />
                <input
                  type="date"
                  onChange={handleDobSearch}
                  value={searchdob}
                  className="h-9 pl-10 pr-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-xs font-medium bg-slate-50 border-slate-200 text-slate-700 focus:bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:focus:bg-slate-600"
                />
              </div>
              <button
                onClick={() => onDobSearch()}
                className="h-9 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center gap-1.5 shadow-sm hover:shadow-md active:scale-95 text-xs bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white dark:from-indigo-600 dark:to-indigo-700 dark:hover:from-indigo-700 dark:hover:to-indigo-800"
              >
                <SearchIcon sx={{ fontSize: 16 }} />
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border transition-colors bg-slate-100 border-slate-200 dark:bg-slate-700 dark:border-slate-600">
              <PeopleAltIcon className="text-indigo-500 dark:text-indigo-400" sx={{ fontSize: 18 }} />
              <span className="font-bold text-xs text-slate-800 dark:text-slate-200">{count}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Patients</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="rounded-2xl p-8 text-center transition-colors bg-white dark:bg-slate-800">
            <Circles height="40" width="40" color="#6366f1" ariaLabel="loading" />
          </div>
        ) : error ? (
          <div className="rounded-2xl p-8 text-center text-red-500 transition-colors bg-white dark:bg-slate-800">
            {error}
          </div>
        ) : data?.length === 0 ? (
          <div className="rounded-2xl py-12 text-center shadow-md transition-colors bg-white dark:bg-slate-800 dark:border dark:border-slate-700">
            <PeopleAltIcon className="text-slate-300 dark:text-slate-500" sx={{ fontSize: 48 }} />
            <p className="text-slate-500 font-medium dark:text-slate-400">No inactive patients found</p>
          </div>
        ) : (
          mobileCards
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block rounded-[8px] shadow-lg border overflow-hidden transition-colors bg-white border-slate-100 shadow-slate-200/50 dark:bg-slate-800 dark:border-slate-700 dark:shadow-slate-900/50">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80 dark:bg-slate-700/80">
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Reg No
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Full Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  DOB
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Address
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Mobile
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <Circles height="40" width="40" color="#6366f1" ariaLabel="loading" />
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              ) : data?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <PeopleAltIcon
                      className="text-slate-300 dark:text-slate-500"
                      sx={{ fontSize: 48 }}
                    />
                    <p className="text-slate-500 font-medium dark:text-slate-400">No inactive patients found</p>
                  </td>
                </tr>
              ) : (
                renderedTableRows
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <BottomTab title="Patient" count={count} onPageChange={onPageChange} />

      {/* Patient View Modal */}
      <PatientViewModal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        patientData={viewPatientData}
        loading={loadingPatient}
        theme={theme}
      />
    </div>
  );
}

export default DeactivePatient;
